import cn from 'classnames';
import { useEffect, useState } from 'react';
import { MIN_SEARCH_LENGTH } from '#api/hosts.ts';
import { Input } from '#components/atoms/forms/input/Input.tsx';
import { useDebouncedCallback } from '#hooks/useDebouncedCallback.ts';
import { useHostFilters } from '#hooks/useHostFilters.ts';
import styles from './HostSearch.module.css';

const SEARCH_DEBOUNCE_MS = 150;

type Props = {
  /** When the header stacks, fill the column width instead of the fixed row width. Driven by
   * HostsPage's single `isStacked` boolean rather than a CSS threshold of our own, so the width
   * can't flip at a different point than the stack. */
  fullWidth?: boolean;
  /** Placeholder and accessible name for the input ("Search hosts" / "Search lighthouses" / …). */
  label?: string;
};

/**
 * {@link HostSearch} owns the host-list search input. It keeps the input responsive locally and
 * commits the trimmed term to the URL (via useHostFilters) once typing settles, so HostsPage can
 * read the committed term from the URL to drive its query. The input concern lives entirely here
 * rather than being threaded through the page.
 */
export function HostSearch({ fullWidth = false, label = 'Search hosts' }: Props) {
  const { search: urlSearch, setSearch } = useHostFilters();
  const [searchQuery, setSearchQuery] = useState(urlSearch);

  // Commit the trimmed term once typing settles, from the change handler rather than an effect
  // watching a debounced value. A value-watching effect would re-fire whenever urlSearch changes —
  // including when the term is cleared elsewhere — and re-commit the stale debounced value, fighting
  // the clear. `setSearch` also resets the cursor (see useHostFilters). Terms below the minimum clear
  // the search, since the API 400s on them. `lastPushed` records what we wrote so the sync below can
  // tell our own writes apart from external ones.
  const [lastPushed, setLastPushed] = useState(urlSearch);
  const commitSearch = useDebouncedCallback((value: string) => {
    const trimmed = value.trim();
    const next = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : '';
    setLastPushed(next);
    setSearch(next);
  }, SEARCH_DEBOUNCE_MS);

  // When the URL term changes from outside this component — e.g. the empty-state "Clear filters" link
  // navigates to the list's base path and drops the term while keeping the network filter — sync the
  // input to match. Comparing against the value we last pushed avoids clobbering in-flight typing when
  // our own debounced write echoes back through the URL.
  const [prevUrlSearch, setPrevUrlSearch] = useState(urlSearch);
  if (urlSearch !== prevUrlSearch) {
    setPrevUrlSearch(urlSearch);
    if (urlSearch !== lastPushed) {
      setSearchQuery(urlSearch);
    }
  }

  // Drop a pending commit when the term changes externally, so a keystroke debounced just before the
  // change can't fire afterward and re-apply a stale search over the clear. Keyed on the committed
  // `urlSearch`/`lastPushed` — NOT a derived "isExternalChange" flag, which the render-phase sync above
  // resets to false before any committed render observes it, so an effect on it never runs. While the
  // user types, `urlSearch`/`lastPushed` don't change (only the local input does), so the pending timer
  // survives; it's only dropped when the URL term changes to something we didn't push.
  useEffect(() => {
    if (urlSearch !== lastPushed) commitSearch.cancel();
  }, [urlSearch, lastPushed, commitSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    commitSearch(e.target.value);
  };

  return (
    <div
      className={cn(styles.HostSearch_wrapper, {
        [styles.HostSearch_wrapper___fullWidth]: fullWidth,
      })}
    >
      <svg
        className={styles.HostSearch_icon}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="6.5" cy="6.5" r="5" />
        <line x1="10" y1="10" x2="14.5" y2="14.5" />
      </svg>
      <Input
        type="search"
        placeholder={label}
        value={searchQuery}
        onChange={handleSearchChange}
        className={styles.HostSearch_input}
        aria-label={label}
      />
    </div>
  );
}
